import { FileText, BookOpen, Presentation, ScrollText, Lightbulb, StickyNote, Layers, ClipboardList, Crown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSubscription } from "@/hooks/useSubscription";
import { useToast } from "@/hooks/use-toast";

export type ProcessingType = 'extract' | 'assignment' | 'presentation' | 'long-notes' | 'short-notes' | 'explain' | 'flashcards' | 'quiz';

interface ProcessingOption {
  type: ProcessingType;
  label: string;
  description: string;
  icon: React.ReactNode;
  isPremium?: boolean;
}

const options: ProcessingOption[] = [
  {
    type: 'extract',
    label: 'Extract Text',
    description: 'Convert handwritten/printed notes to digital text',
    icon: <FileText className="w-5 h-5" />,
  },
  {
    type: 'assignment',
    label: 'Assignment Answers',
    description: 'Generate answers from your notes',
    icon: <BookOpen className="w-5 h-5" />,
  },
  {
    type: 'presentation',
    label: 'Create Presentation',
    description: 'Generate PowerPoint slides outline',
    icon: <Presentation className="w-5 h-5" />,
  },
  {
    type: 'long-notes',
    label: 'Detailed Notes',
    description: 'Comprehensive study notes for exam prep',
    icon: <ScrollText className="w-5 h-5" />,
  },
  {
    type: 'short-notes',
    label: 'Quick Notes',
    description: 'Concise revision notes & key points',
    icon: <StickyNote className="w-5 h-5" />,
  },
  {
    type: 'explain',
    label: 'Explain Topics',
    description: 'Simple explanations of concepts',
    icon: <Lightbulb className="w-5 h-5" />,
  },
  {
    type: 'flashcards',
    label: 'Generate Flashcards',
    description: 'Create study flashcards for revision',
    icon: <Layers className="w-5 h-5" />,
    isPremium: true,
  },
  {
    type: 'quiz',
    label: 'Generate Quiz',
    description: 'Create MCQ quiz to test your knowledge',
    icon: <ClipboardList className="w-5 h-5" />,
    isPremium: true,
  },
];

interface ProcessingOptionsProps {
  onSelect: (type: ProcessingType) => void;
  disabled?: boolean;
}

const ProcessingOptions = ({ onSelect, disabled }: ProcessingOptionsProps) => {
  const { isPro, isPremium, loading } = useSubscription();
  const { toast } = useToast();
  
  const hasPremiumAccess = isPro || isPremium;

  const handleSelect = (option: ProcessingOption) => {
    if (option.isPremium && !hasPremiumAccess) {
      toast({
        title: "Pro Feature",
        description: "Upgrade to Pro or Premium to access flashcards and quizzes.",
      });
      document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
      return;
    }
    onSelect(option.type);
  };

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
      {options.map((option) => (
        <Button
          key={option.type}
          variant="outline"
          onClick={() => handleSelect(option)}
          disabled={disabled || loading}
          className={`h-auto min-h-[120px] py-4 px-4 flex flex-col items-center justify-start gap-2 hover:border-primary hover:bg-primary/5 transition-all whitespace-normal relative ${
            option.isPremium && !hasPremiumAccess ? 'opacity-75' : ''
          }`}
        >
          {option.isPremium && (
            <div className="absolute top-2 right-2">
              <Crown className={`w-4 h-4 ${hasPremiumAccess ? 'text-primary' : 'text-yellow-600 dark:text-yellow-500'}`} />
            </div>
          )}
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary shrink-0">
            {option.icon}
          </div>
          <div className="text-center w-full overflow-hidden">
            <p className="font-medium text-sm truncate">{option.label}</p>
            <p className="text-xs text-muted-foreground mt-1 line-clamp-2 break-words">
              {option.description}
            </p>
            {option.isPremium && !hasPremiumAccess && (
              <p className="text-xs text-yellow-600 dark:text-yellow-500 mt-1 font-medium">Pro/Premium</p>
            )}
          </div>
        </Button>
      ))}
    </div>
  );
};

export default ProcessingOptions;
